import './ManageAuthors.css';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchJournals, deleteBook, searchBooks } from '../../services/bookService';
import Notification from '../Notification/Notification';
import Pagination from '../Pagination/Pagination';

const BOOKS_PER_PAGE = 12;

function ManageBooks() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [books, setBooks] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');

    // Check for success message from navigation
    useEffect(() => {
        if (location.state?.message) {
            setNotificationMessage(location.state.message);
            setShowNotification(true);
            // Clear the state so notification doesn't show again on refresh
            window.history.replaceState({}, document.title);
        }
    }, [location]);

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(0); // Reset to first page on new search
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    const loadBooks = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            let pageData;
            if (debouncedSearchTerm.trim()) {
                // Use search endpoint if there's a search term
                pageData = await searchBooks(debouncedSearchTerm.trim(), currentPage, BOOKS_PER_PAGE);
            } else {
                // Use regular fetch if no search term
                pageData = await fetchJournals(currentPage, BOOKS_PER_PAGE);
            }

            // Handle both paginated and non-paginated responses
            if (pageData.content) {
                setBooks(pageData.content);
                setTotalPages(pageData.page?.totalPages || 1);
                setTotalElements(pageData.page?.totalElements || pageData.content.length);
            } else if (Array.isArray(pageData)) {
                setBooks(pageData);
                setTotalPages(1);
                setTotalElements(pageData.length);
            } else {
                setBooks([]);
                setTotalPages(1);
                setTotalElements(0);
            }
        } catch (err) {
            setError('Nepodařilo se načíst knihy. Zkuste to prosím znovu.');
            console.error('Error loading books:', err);
        } finally {
            setLoading(false);
        }
    }, [currentPage, debouncedSearchTerm]);

    useEffect(() => {
        loadBooks();
    }, [loadBooks]);

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleBack = () => {
        navigate('/admin');
    };

    const handleEdit = (bookId) => {
        navigate(`/admin/edit-book/${bookId}`);
    };

    const handleDeleteClick = (book) => {
        setDeleteConfirm(book);
    };

    const handleDeleteCancel = () => {
        setDeleteConfirm(null);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteConfirm) return;

        try {
            setDeleting(true);
            await deleteBook(deleteConfirm.id);

            setDeleteConfirm(null);

            // Reload the current page to get updated data
            await loadBooks();
        } catch (err) {
            setError('Nepodařilo se smazat knihu. Zkuste to prosím znovu.');
            console.error('Error deleting book:', err);
        } finally {
            setDeleting(false);
        }
    };

    const handleCloseNotification = () => {
        setShowNotification(false);
    };

    return (
        <div className="admin-page">
            <div className="manage-container">
                <button className="back-button" onClick={handleBack}>
                    ← Zpět na dashboard
                </button>

                <h1 className="page-title">Spravovat knihy</h1>

                {showNotification && (
                    <Notification
                        message={notificationMessage}
                        type="success"
                        onClose={handleCloseNotification}
                        duration={2000}
                    />
                )}

                <div className="search-bar-container">
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Hledat knihy podle názvu..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <svg
                        className="search-icon"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    >
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="m21 21-4.35-4.35"></path>
                    </svg>
                </div>

                {error && (
                    <div className="alert alert-error">
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="loading-container">
                        <p>Načítání knih...</p>
                    </div>
                ) : books.length === 0 ? (
                    <div className="no-authors">
                        <p>Zatím nejsou žádné knihy v databázi.</p>
                    </div>
                ) : (
                    <div className="authors-list">
                        {books.map(book => (
                            <div key={book.id} className="author-item">
                                <div className="author-info">
                                    <h3 className="author-name">{book.bookTitle}</h3>
                                    {book.bookType && (
                                        <p className="author-dates">{book.bookType}</p>
                                    )}
                                </div>
                                <div className="author-actions">
                                    <button
                                        className="btn-action btn-edit"
                                        onClick={() => handleEdit(book.id)}
                                        title="Upravit knihu"
                                    >
                                        ✏️ Upravit
                                    </button>
                                    <button
                                        className="btn-action btn-delete"
                                        onClick={() => handleDeleteClick(book)}
                                        title="Smazat knihu"
                                    >
                                        🗑️ Smazat
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!loading && !error && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalElements={totalElements}
                        onPageChange={handlePageChange}
                        itemLabel="knih"
                    />
                )}

                {/* Delete Confirmation Modal */}
                {deleteConfirm && (
                    <div className="modal-overlay" onClick={handleDeleteCancel}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <h2 className="modal-title">Potvrdit smazání</h2>
                            <p className="modal-text">
                                Opravdu chcete smazat knihu <strong>{deleteConfirm.bookTitle}</strong>?
                            </p>
                            <p className="modal-warning">
                                Tato akce je nevratná!
                            </p>
                            <div className="modal-actions">
                                <button
                                    className="btn btn-secondary"
                                    onClick={handleDeleteCancel}
                                    disabled={deleting}
                                >
                                    Zrušit
                                </button>
                                <button
                                    className="btn btn-danger"
                                    onClick={handleDeleteConfirm}
                                    disabled={deleting}
                                >
                                    {deleting ? 'Mazání...' : 'Smazat'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ManageBooks;
